import {useMutation} from '@apollo/react-hooks';

import type {
    TimelineEventDelete as TimelineEventDeleteMutation,
    TimelineEventDeleteVariables as TimelineEventDeleteMutationVariables,
    SetPinnedEntity as SetPinnedEntityMutation,
    SetPinnedEntityVariables as SetPinnedEntityMutationVariables,
    SetPinnedEntity_setEntityPinnedStatus_pinnedInfo as PinnedInfo,
    GetEvents as GetEventsQuery,
    GetEventsVariables as GetEventsQueryVariables,
    GetPinnedEvents as GetPinnedEventsQuery,
    GetPinnedEvents_pinnedEvents_edges as PinnedEventsEdge,
} from 'nutshell-graphql-types';

import EVENT_FRAGMENT_SPARSE from './fragments/event-fragment-sparse.graphql';
import DELETE_EVENT from './mutations/timeline-event-delete.graphql';
import SET_PINNED_STATUS from './mutations/timeline-event-set-pinned-status.graphql';
import GET_EVENTS from './queries/get-events.graphql';
import GET_PINNED_EVENTS from './queries/get-pinned-events.graphql';

/*
This hook is used to delete events with optimistic response. It can be used on
entity pages or the dashboard, but will not work in the note/email/chat modals because
we won't be able to pass in the query variables from the modals to this function.
*/
export function useDeleteEvent(): (arg1: {
    changeLogId: string;
    queryVariables?: GetEventsQueryVariables;
}) => Promise<any> {
    const [deleteTimelineEvent] = useMutation<
        TimelineEventDeleteMutation,
        TimelineEventDeleteMutationVariables
    >(DELETE_EVENT);

    return (input: {changeLogId: string; queryVariables?: GetEventsQueryVariables}) => {
        return deleteTimelineEvent({
            variables: {input: {eventId: input.changeLogId}},
            optimisticResponse: {
                timelineEventDelete: {
                    __typename: 'TimelineEventDeletePayload',
                    eventId: input.changeLogId,
                },
            },
            update: (proxy) => {
                const cacheResult: GetEventsQuery | null | undefined = proxy.readQuery({
                    query: GET_EVENTS,
                    variables: input.queryVariables,
                });

                if (cacheResult && cacheResult.events) {
                    proxy.writeQuery({
                        query: GET_EVENTS,
                        variables: input.queryVariables,
                        data: {
                            events: {
                                ...cacheResult.events,
                                edges: cacheResult.events.edges.filter((edge) => {
                                    return edge.node.id !== input.changeLogId;
                                }),
                            },
                        },
                    });
                }
            },
        });
    };
}

/*
This hook is used to delete events WITHOUT optimistic response. It will work in the
note/email/chat modals.
*/
export function useDeleteEventForModal(): (arg1: {changeLogId: string}) => Promise<any> {
    const [deleteTimelineEvent] = useMutation<
        TimelineEventDeleteMutation,
        TimelineEventDeleteMutationVariables
    >(DELETE_EVENT);

    return (input: {changeLogId: string}) => {
        return deleteTimelineEvent({
            variables: {input: {eventId: input.changeLogId}},
            refetchQueries: ['GetEvents'],
        }).then(
            (mutationResults) =>
                mutationResults &&
                mutationResults.data &&
                mutationResults.data.timelineEventDelete &&
                mutationResults.data.timelineEventDelete.eventId
        );
    };
}

export function useSetPinnedStatus(): (arg1: {
    setStatusInput: {
        entityToPinId: string;
        pinToEntityId: string;
        status: boolean;
        userName: string;
        changeLogId: string;
    };
    queryVariables?: GetEventsQueryVariables;
}) => Promise<any> {
    const [setPinnedStatus] = useMutation<
        SetPinnedEntityMutation,
        SetPinnedEntityMutationVariables
    >(SET_PINNED_STATUS);

    return (input: {
        setStatusInput: {
            entityToPinId: string;
            pinToEntityId: string;
            status: boolean;
            userName: string;
            changeLogId: string;
        };
        queryVariables?: GetEventsQueryVariables;
    }) => {
        return setPinnedStatus({
            variables: {
                input: {
                    entityToPinId: input.setStatusInput.entityToPinId,
                    pinToEntityId: input.setStatusInput.pinToEntityId,
                    status: input.setStatusInput.status,
                },
            },
            optimisticResponse: {
                setEntityPinnedStatus: {
                    __typename: 'SetEntityPinnedStatusPayload',
                    pinnedInfo: input.setStatusInput.status
                        ? {
                              __typename: 'PinnedInfo',
                              pinnedTime: '',
                              pinnedByUser: {
                                  __typename: 'User',
                                  id: 'temp',
                                  name: input.setStatusInput.userName,
                              },
                          }
                        : null,
                },
            },
            update: (proxy, mutationResults) => {
                const cacheResult: GetEventsQuery | null | undefined = proxy.readQuery({
                    query: GET_EVENTS,
                    variables: input.queryVariables,
                });

                const pinnedInfo =
                    mutationResults &&
                    mutationResults.data &&
                    mutationResults.data.setEntityPinnedStatus &&
                    mutationResults.data.setEntityPinnedStatus.pinnedInfo;

                if (cacheResult && cacheResult.events) {
                    proxy.writeQuery({
                        query: GET_EVENTS,
                        variables: input.queryVariables,
                        data: {
                            events: {
                                ...cacheResult.events,
                                edges: cacheResult.events.edges.map((edge) => {
                                    if (
                                        // @ts-expect-error
                                        edge.node.payload.id === input.setStatusInput.entityToPinId
                                    ) {
                                        return {...edge, pinnedInfo};
                                    }

                                    return edge;
                                }),
                            },
                        },
                    });
                }

                const pinnedEventsCacheResult: GetPinnedEventsQuery | null | undefined =
                    proxy.readQuery({
                        query: GET_PINNED_EVENTS,
                        variables: {input: {entityId: input.setStatusInput.pinToEntityId}},
                    });

                const updatedEvent = proxy.readFragment({
                    id: input.setStatusInput.changeLogId,
                    fragmentName: 'EventFragmentSparse',
                    fragment: EVENT_FRAGMENT_SPARSE,
                });

                if (pinnedEventsCacheResult && pinnedEventsCacheResult.pinnedEvents) {
                    proxy.writeQuery({
                        query: GET_PINNED_EVENTS,
                        variables: {input: {entityId: input.setStatusInput.pinToEntityId}},
                        data: {
                            pinnedEvents: {
                                ...pinnedEventsCacheResult.pinnedEvents,
                                edges: getUpdatedPinnedEventsEdges({
                                    edges: pinnedEventsCacheResult.pinnedEvents.edges,
                                    pinnedEntityId: input.setStatusInput.entityToPinId,
                                    updatedEvent,
                                    pinnedInfo,
                                }),
                            },
                        },
                    });
                }
            },
        });
    };
}

const getUpdatedPinnedEventsEdges = (input: {
    edges: PinnedEventsEdge[];
    pinnedEntityId: string;
    updatedEvent: any;
    pinnedInfo: PinnedInfo | null | undefined;
}): PinnedEventsEdge[] => {
    if (!input.pinnedInfo) {
        return input.edges.filter(
            // @ts-expect-error
            (edge) => edge.node.payload.id && edge.node.payload.id !== input.pinnedEntityId
        );
    } else if (input.updatedEvent) {
        return input.edges.concat({
            __typename: 'TimelineEventsEdge',
            node: input.updatedEvent,
            pinnedInfo: input.pinnedInfo,
        });
    } else {
        return input.edges;
    }
};
